import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy, OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { forkJoin, of, ReplaySubject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { Ofwca } from '../../interface/ofwca';
import { ListResponse } from '../../interface/response/list-response';
import { OfwcaService } from '../../services/ofwca.service';
import {AffiliateCode} from "../../interface/affiliateCode";

@Component({
  selector: 'app-affiliate-code-dobry-agent-select',
  templateUrl: './affiliate-code-select.component.html',
  styleUrls: ['./affiliate-code-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AffiliateCodeSelectComponent),
      multi: true,
    },
  ],
})
export class AffiliateCodeSelectComponent implements OnChanges, OnDestroy, ControlValueAccessor {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() label = 'Kod zniżkowy Chronie.pl';
  @Input() placeholder = 'Wybierz kod zniżkowy';
  @Input() ofwcas: Ofwca[] = [];
  @Input() required = false;

  items: AffiliateCode[] = [];
  filteredItems: AffiliateCode[] = [];

  value: string | null = null;
  disabled = false;
  loading = false;

  private lastOfwcaKey = '';

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private ofwcaService: OfwcaService,
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.ofwcas) {
      this.loadAffiliateCodesForOfwcas();
    }
  }

  writeValue(value: string | null): void {
    this.value = value ?? null;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  changeValue(value: string | null): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  compareByCode(a: string | AffiliateCode, b: string | AffiliateCode): boolean {
    const first = typeof a === 'object' ? a?.code : a;
    const second = typeof b === 'object' ? b?.code : b;

    return first === second;
  }

  getOptionLabel(item: AffiliateCode): string {
    const ownerName = [
      item.ofwca?.fullName
    ].filter(Boolean).join(' ');

    const partnerName = item.partner?.fullName
      ?? item.partner?.shortName
      ?? item.partner?.fullName
      ?? '';

    const parts = [
      partnerName,
      ownerName,
    ].filter(Boolean);

    return parts.join(' — ');
  }

  private loadAffiliateCodesForOfwcas(): void {
    const ofwcaUuids = (this.ofwcas ?? [])
      .map((ofwca: any) => ofwca.uuid)
      .filter(Boolean);

    const currentKey = [...ofwcaUuids].sort().join('|');

    if (currentKey === this.lastOfwcaKey) {
      return;
    }

    this.lastOfwcaKey = currentKey;

    if (!ofwcaUuids.length) {
      this.items = [];
      this.filteredItems = [];
      return;
    }

    this.loading = true;

    const requests = ofwcaUuids.map((ofwcaUuid: string) => {
      return this.ofwcaService.getAffiliateCode(ofwcaUuid)
        .pipe(
          map((response: ListResponse<AffiliateCode>) => response.items ?? []),
          catchError(() => of([] as AffiliateCode[])),
        );
    });

    forkJoin(requests)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((responses: AffiliateCode[][]) => {
        const flattened = responses.reduce((items, current) => {
          return [...items, ...current];
        }, [] as AffiliateCode[]);

        this.items = this.uniqueAffiliateCodes(flattened)
          .filter((item) => !item.deleted)
          .sort((a, b) => {
            if (a.main !== b.main) {
              return a.main ? -1 : 1;
            }

            return (a.code ?? '').localeCompare(b.code ?? '');
          });

        this.filteredItems = this.items;
        this.loading = false;
      });
  }

  private uniqueAffiliateCodes(items: AffiliateCode[]): AffiliateCode[] {
    const mapByCode = new Map<string, AffiliateCode>();

    items.forEach((item) => {
      if (!item?.code) {
        return;
      }

      if (!mapByCode.has(item.code)) {
        mapByCode.set(item.code, item);
        return;
      }

      const existing = mapByCode.get(item.code);

      if (!existing?.main && item.main) {
        mapByCode.set(item.code, item);
      }
    });

    return Array.from(mapByCode.values());
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
